import type {
  AnalysisStateView,
  DecisionActionBlocker,
  DraftContinuationStatus,
  DraftView,
  PositionOccasion,
  PositionStaffingFormat,
  ConversationListItem,
} from "../api/types";

/**
 * The worklist is already ordered by the server. Continue with its first still-open request the
 * operator may actually work on — one they are on directly, or through a team (#803).
 *
 * Before the assignment set, an UNASSIGNED request was a fine place to land: the first action
 * claimed it. Now it is the one place auto-advance must not land, because the operator would
 * arrive at a request they can read and nothing else. Answered/sent requests are not the next
 * unit of work either, even though they remain visible in the inbox until Mark Done.
 *
 * `own` is the caller's own principal keys — `user:<id>` plus every `team:<id>` they are in.
 */
export function nextOpenConversationRoute(
  rows: readonly ConversationListItem[],
  currentRequestId: string,
  own: ReadonlySet<string>,
): string | null {
  if (!own.size) return null;
  const next = rows.find((row) =>
    row.target_request_id !== currentRequestId
    && row.lifecycle_status !== "answered"
    && row.lifecycle_status !== "done"
    && row.assignees.some((principal) => own.has(`${principal.kind}:${principal.id}`)),
  );
  return next ? String(next.target_request_no ?? next.target_request_id) : null;
}

export function draftContinuationMessageKey(
  status: DraftContinuationStatus | null | undefined,
): string {
  switch (status) {
    case "unchanged":
      return "ws.autoDraftUnchanged";
    case "drafted":
      return "ws.autoDraftCreated";
    case "skipped_existing_draft":
      return "ws.autoDraftExisting";
    case "already_generating":
      return "ws.autoDraftAlreadyGenerating";
    case "skipped_human_review":
    case "not_draftable":
      return "ws.autoDraftNeedsReview";
    case "skipped_delivery":
      return "ws.autoDraftSkippedDelivery";
    case "skipped_commercial_defer":
      return "ws.autoDraftCommercialDefer";
    case "no_decision":
      return "ws.autoDraftNoDecision";
    case "ai_error":
      return "ws.autoDraftFailed";
    default:
      // Rolling clients can briefly meet an older Core response without draft_status.
      return "ws.autoDraftFailed";
  }
}

export type ActionDraftRef = {
  id?: string | null;
  is_current_decision?: boolean | null;
} | null | undefined;

const HAMBURG_CUTOFF_CUSTOMER_NOTICE_IDS = new Set([
  "HAMBURG_LATE_INITIAL_SUBMISSION",
  "HAMBURG_LATE_MATERIAL_CHANGE",
  "HAMBURG_LATE_MIXED_CHANGE",
  "HAMBURG_ONLINE_SHOP_CHANGE_FORBIDDEN",
]);

export function hasHamburgCutoffCustomerNotice(
  state?: {
    required_notices?: readonly string[] | null;
    requires_human_review?: boolean | null;
    quote_eligible?: boolean | null;
    response_type?: string | null;
    forbidden_actions?: readonly string[] | null;
    review_reasons?: readonly string[] | null;
    commercial_outcome?: {
      cutoff?: {
        outcome?: string | null;
        reason_code?: string | null;
        kind?: string | null;
        order_submitted_at?: string | null;
        applied_changes?: readonly unknown[] | null;
        rejected_changes?: readonly unknown[] | null;
      } | null;
    } | null;
  } | null,
): boolean {
  const cutoff = state?.commercial_outcome?.cutoff;
  if (state?.requires_human_review !== true || !cutoff) return false;
  if (
    state.response_type !== "handover_human"
    || state.quote_eligible !== false
    || !state.forbidden_actions?.includes("create_individual_export")
    || !state.review_reasons?.includes("hamburg_cutoff:" + cutoff.reason_code)
  ) {
    return false;
  }
  const notices = (state.required_notices ?? []).filter((id) =>
    HAMBURG_CUTOFF_CUSTOMER_NOTICE_IDS.has(id)
  );
  if (notices.length !== 1) return false;
  const expected = cutoff.outcome === "LATE_INITIAL_SUBMISSION"
      && cutoff.reason_code === "INITIAL_ORDER_DEADLINE_PASSED"
      && cutoff.kind === "INITIAL_SUBMISSION"
    ? "HAMBURG_LATE_INITIAL_SUBMISSION"
    : cutoff.outcome === "CHANGE_FORBIDDEN"
        && cutoff.reason_code === "ONLINE_SHOP_ORDER_CHANGE_FORBIDDEN"
        && cutoff.kind === "ONLINE_SHOP_CHANGE"
        && cutoff.order_submitted_at != null
        && (cutoff.rejected_changes?.length ?? 0) > 0
      ? "HAMBURG_ONLINE_SHOP_CHANGE_FORBIDDEN"
      : cutoff.outcome === "LATE_MATERIAL_CHANGE"
          && cutoff.reason_code === "MATERIAL_CHANGE_DEADLINE_PASSED"
          && cutoff.kind === "MATERIAL_CHANGE"
          && (cutoff.rejected_changes?.length ?? 0) > 0
        ? (cutoff.applied_changes?.length ?? 0) > 0
          ? "HAMBURG_LATE_MIXED_CHANGE"
          : "HAMBURG_LATE_MATERIAL_CHANGE"
        : null;
  return expected !== null && notices[0] === expected;
}

/** What the draft pane should show when there is no draft yet (#678). */
export type DraftPaneEmptyState = "blocked" | "generating" | "ai_off" | "idle";

/**
 * #678: work in flight IS the answer to "where is my draft" — the draft is on its way. Showing
 * the idle "no draft yet, click Generate" panel there reads as a failure and invites a manual
 * run that competes with the one already going.
 *
 * Extraction failure is a technical block; business review leaves draft work available.
 * AI being off outranks the idle prompt because generation still needs the capability.
 */
export function draftPaneEmptyState(input: {
  extractionFailed: boolean;
  generating: boolean;
  aiOn: boolean;
}): DraftPaneEmptyState {
  if (input.extractionFailed) return "blocked";
  if (input.generating) return "generating";
  if (!input.aiOn) return "ai_off";
  return "idle";
}

/**
 * True while the server-side chain owns the request (#678). This complements
 * `isDraftGenerationInProgress`, which only sees the drafting stage through
 * `request.status === "draft_generating"`: a request sitting in extraction or decision is just
 * as busy, and a manual generate there would race the run that is about to produce the draft.
 */
export function analysisRunInFlight(analysisState?: string | null): boolean {
  return analysisState === "queued" || analysisState === "running";
}

/**
 * Whether the manual generate/regenerate control is unavailable. Extracted from the action bar
 * so the one rule that matters here is testable: a run already in flight disables it (#678),
 * because a second run would race the one about to produce the draft.
 */
export function generateControlDisabled(input: {
  aiOn: boolean;
  generating: boolean;
  analysisInFlight: boolean;
  decisionActionsBlocked: boolean;
  terminal: boolean;
  dirty: boolean;
  reconciliationPending: boolean;
  busy: boolean;
}): boolean {
  return !input.aiOn
    || input.generating
    || input.analysisInFlight
    || input.decisionActionsBlocked
    || input.terminal
    || input.dirty
    || input.reconciliationPending
    || input.busy;
}

export type ActionDeliveryRef = {
  state?: string | null;
  draft_id?: string | null;
} | null | undefined;

export function isTerminalDeliveryForDraft(
  draft: ActionDraftRef,
  delivery: ActionDeliveryRef,
): boolean {
  const state = delivery?.state;
  if (state !== "SENT" && state !== "UNKNOWN") return false;
  if (!draft?.id) return true;
  return delivery?.draft_id === draft.id;
}

export type DecisionSafetyNoticeKind =
  | "danger_safety"
  | "info_no_decision"
  | "info_newer_message"
  | null;

export function newerInboundRevisionPresent(input: {
  lifecycleStatus: string;
  currentRequestRevision: number;
  decisionRequestRevision?: number | null;
}): boolean {
  return input.lifecycleStatus === "new_message"
    && Number.isSafeInteger(input.currentRequestRevision)
    && Number.isSafeInteger(input.decisionRequestRevision)
    && input.decisionRequestRevision! < input.currentRequestRevision;
}

export function draftWasOvertakenByNewerInbound(input: {
  lifecycleStatus: string;
  currentRequestRevision: number;
  draft: Pick<
    DraftView,
    "review_state" | "request_revision" | "stale_reason"
  > | null | undefined;
}): boolean {
  return input.lifecycleStatus === "new_message"
    && input.draft?.stale_reason === "request_revision_changed"
    && Number.isSafeInteger(input.currentRequestRevision)
    && Number.isSafeInteger(input.draft.request_revision)
    && input.draft.request_revision! < input.currentRequestRevision;
}

export type NewerMessageNoticeBodyKey =
  | "ws.newerMessageNoticeRunning"
  | "ws.newerMessageNoticeWaiting"
  | "ws.newerMessageNoticeHumanReview"
  | "ws.newerMessageNoticeFailed";

export function newerMessageNoticeBodyKey(input: {
  analysis: Pick<AnalysisStateView, "state" | "reason_code"> | null | undefined;
  humanReviewReasonPresent: boolean;
}): NewerMessageNoticeBodyKey {
  if (
    input.humanReviewReasonPresent ||
    (input.analysis?.state === "blocked" && input.analysis.reason_code === "human_review_required")
  ) {
    return "ws.newerMessageNoticeHumanReview";
  }
  if (input.analysis?.state === "running") return "ws.newerMessageNoticeRunning";
  if (input.analysis?.state === "retryable_failed") return "ws.newerMessageNoticeFailed";
  return "ws.newerMessageNoticeWaiting";
}

/**
 * Which decision notice the workspace shows (#581/#587). "No decision yet" and the sole
 * revision mismatch caused by a recorded newer-message lifecycle are expected processing
 * states, not safety failures. Both downgrades require strictly validated guidance; the
 * newer-message path additionally requires validated action blockers and an active analysis
 * projection. Terminal requests need no decision notice. Every unrecognized or mixed shape
 * stays loud, and action enforcement is unaffected by this presentation selection.
 */
export function decisionSafetyNoticeKind(input: {
  decisionPresent: boolean;
  decisionActionsBlocked: boolean;
  safetyEvidenceValid: boolean;
  decisionActionBlockers: readonly DecisionActionBlocker[];
  guidanceEvidenceValid: boolean;
  guidanceState: string;
  guidanceSummaryCode: string;
  pipelineNoticePresent: boolean;
  newerInboundRevisionPresent: boolean;
  analysisState: AnalysisStateView["state"] | null;
}): DecisionSafetyNoticeKind {
  if (!input.decisionActionsBlocked) return null;
  if (input.decisionPresent) {
    const expectedNewerMessage = input.safetyEvidenceValid
      && input.guidanceEvidenceValid
      && input.guidanceState === "blocked"
      && input.guidanceSummaryCode === "DECISION_SAFETY_BLOCKED"
      && input.newerInboundRevisionPresent
      && input.analysisState !== null
      && ["queued", "running", "blocked", "retryable_failed"].includes(input.analysisState)
      && input.decisionActionBlockers.length === 1
      && ["request_revision_stale", "request_input_stale"].includes(input.decisionActionBlockers[0]);
    return expectedNewerMessage ? "info_newer_message" : "danger_safety";
  }
  if (!input.guidanceEvidenceValid) return "danger_safety";
  if (input.guidanceState === "complete") {
    // The guidance validator pins "complete" to exactly these summaries; require them here
    // too so this function stays loud on any shape it does not recognize by itself.
    return input.guidanceSummaryCode === "SENT" ||
        input.guidanceSummaryCode === "CLOSED_REQUEST"
      ? null
      : "danger_safety";
  }
  if (input.guidanceSummaryCode !== "MISSING_DECISION") return "danger_safety";
  // The pipeline notice already explains why analysis has not produced a decision here;
  // stacking "no decision yet" on top of it would restate the same fact.
  return input.pipelineNoticePresent ? null : "info_no_decision";
}

export function normalizeCloseReason(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function isDraftGenerationInProgress(
  requestStatus: string | null | undefined,
  localGenerating: boolean,
): boolean {
  return localGenerating || requestStatus === "draft_generating";
}

export type DraftEditorRefreshDraft = {
  id?: string | null;
  revision?: number | null;
  request_revision?: number | null;
} | null | undefined;

export function shouldResetDraftEditorOnLiveRefresh(
  current: DraftEditorRefreshDraft,
  next: DraftEditorRefreshDraft,
): boolean {
  if (!current && !next) return false;
  if (!current || !next) return true;
  return current.id !== next.id
    || current.revision !== next.revision
    || current.request_revision !== next.request_revision;
}

const POSITION_OCCASIONS = new Set<PositionOccasion>([
  "kurzer_empfang",
  "empfang_premium",
  "lunch",
  "flying_dinner",
  "snack",
  "dinner",
  "premium",
]);

export function draftOfferOccasion(
  draft: Pick<DraftView, "offer_snapshot"> | null | undefined,
): PositionOccasion | null {
  const occasion = draft?.offer_snapshot?.occasion;
  return occasion && POSITION_OCCASIONS.has(occasion) ? occasion : null;
}

/**
 * Hydrate only when the server's active draft identity changes. This restores a generated
 * draft's reviewed occasion after navigation/reload without clobbering a newer in-memory operator
 * selection during unrelated live refreshes of the same draft (or while no draft exists).
 */
export function positionOccasionHydration(
  currentDraftId: string | null | undefined,
  nextDraft: Pick<DraftView, "id" | "offer_snapshot"> | null | undefined,
): { draftId: string | null; occasion: PositionOccasion | null } | null {
  const nextDraftId = nextDraft?.id ?? null;
  if (currentDraftId === nextDraftId) return null;
  return {
    draftId: nextDraftId,
    occasion: draftOfferOccasion(nextDraft),
  };
}

const POSITION_STAFFING_FORMATS = new Set([
  "set_menu",
  "flying",
  "live_cooking_flying",
  "buffet",
]);

export type PositionProposalControls = {
  occasion: PositionOccasion | null;
  staffingFormat: PositionStaffingFormat | null;
  durationHours: number | null;
  includeStaffing: boolean;
};

export type PositionProposalRequest = PositionProposalControls & {
  requestId: string;
  refreshKey?: string | null;
};

/**
 * Identifies the exact request that produced a positions response. This lets the component hide
 * the previous result synchronously when navigation, live refresh, or any proposal control changes,
 * before the effect for the next request has a chance to set its loading flag.
 */
export function positionProposalRequestSignature(
  input: PositionProposalRequest,
): string {
  return JSON.stringify([
    input.requestId,
    input.refreshKey ?? null,
    input.occasion,
    input.staffingFormat,
    input.durationHours,
    input.includeStaffing,
  ]);
}

export type PositionProposalPresentation = {
  summaryKey:
    | "common.loading"
    | "ws.positionsCount"
    | "ws.positionsPartial"
    | "ws.positionsUnavailable";
  summaryCount?: number;
  showEnrichmentWarning: boolean;
};

/**
 * Presentation-only classification of the server proposal. A result is partial only when the
 * server supplied usable positions and also named unresolved enrichment work.
 */
export function positionProposalPresentation(input: {
  loading: boolean;
  eligible: boolean;
  positionCount: number;
  enrichmentCount: number;
}): PositionProposalPresentation {
  if (input.loading) {
    return {
      summaryKey: "common.loading",
      showEnrichmentWarning: false,
    };
  }

  const showEnrichmentWarning = input.enrichmentCount > 0;
  if (!input.eligible || input.positionCount === 0) {
    return {
      summaryKey: "ws.positionsUnavailable",
      showEnrichmentWarning,
    };
  }
  if (showEnrichmentWarning) {
    return {
      summaryKey: "ws.positionsPartial",
      showEnrichmentWarning: true,
    };
  }
  return {
    summaryKey: "ws.positionsCount",
    summaryCount: input.positionCount,
    showEnrichmentWarning: false,
  };
}

function positionProposalControlsFromDraft(
  draft: Pick<DraftView, "offer_snapshot"> | null | undefined,
): PositionProposalControls {
  const snapshot = draft?.offer_snapshot;
  const format = snapshot?.staffingFormat;
  const duration = snapshot?.durationHours;
  return {
    occasion: draftOfferOccasion(draft),
    staffingFormat:
      format && POSITION_STAFFING_FORMATS.has(format) ? format : null,
    durationHours:
      typeof duration === "number" && duration > 0 && duration <= 72
        ? duration
        : null,
    includeStaffing: snapshot ? snapshot.includeStaffing === true : true,
  };
}

export function proposalControlsDifferFromDraft(
  draft: Pick<DraftView, "offer_snapshot"> | null | undefined,
  controls: PositionProposalControls,
): boolean {
  if (!draft) return false;
  const snapshot = positionProposalControlsFromDraft(draft);
  return snapshot.occasion !== controls.occasion
    || snapshot.staffingFormat !== controls.staffingFormat
    || snapshot.durationHours !== controls.durationHours
    || snapshot.includeStaffing !== controls.includeStaffing;
}

export function positionProposalHydration(
  currentDraftId: string | null | undefined,
  nextDraft: Pick<DraftView, "id" | "offer_snapshot"> | null | undefined,
): ({ draftId: string | null } & PositionProposalControls) | null {
  const nextDraftId = nextDraft?.id ?? null;
  if (currentDraftId === nextDraftId) return null;
  return {
    draftId: nextDraftId,
    ...positionProposalControlsFromDraft(nextDraft),
  };
}

export type FactProvenanceRef = {
  sourceType?: string | null;
  source_message_id?: string | null;
  source_message_label?: string | null;
  manually_overridden_by?: string | null;
  override_reason?: string | null;
  overrideReason?: string | null;
};

export type FactProvenanceItem = {
  key: "override_reason";
  labelKey: string;
  value: string;
};

export type SourceMessageOrderRef = {
  id?: string | null;
  direction?: string | null;
};

export type FactSourceDisplayRef = {
  sourceType?: string | null;
  source_message_id?: string | null;
  source_message_label?: string | null;
};

export type FactSourceDisplay = {
  icon: string;
  label: string | null;
  labelKey: string;
};

function nonEmpty(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHORT_HEX_RE = /^[0-9a-f]{8}$/i;
const DERIVED_REQUEST_REFERENCE_RE = /^REQ-[0-9a-f]{8}$/i;

export function operatorRequestReferenceLabel(reference: string | null | undefined): string | null {
  const value = nonEmpty(reference);
  if (!value) return null;
  if (UUID_RE.test(value) || SHORT_HEX_RE.test(value) || DERIVED_REQUEST_REFERENCE_RE.test(value)) {
    return null;
  }
  return value;
}

const SOURCE_ICON_BY_TYPE: Record<string, string> = {
  customer_email: "mail",
  external_lookup: "external",
  manual: "pencil",
  rule_inference: "gauge",
};

export function sourceMessageOrderLabels(
  messages: readonly SourceMessageOrderRef[],
  formatLabel: (oneBasedIndex: number) => string,
): Map<string, string> {
  const labels = new Map<string, string>();
  let sourceIndex = 0;
  messages.forEach((message) => {
    if (message.direction === "outbound") return;
    sourceIndex += 1;
    const id = nonEmpty(message.id ?? undefined);
    if (id) labels.set(id, formatLabel(sourceIndex));
  });
  return labels;
}

export function factSourceDisplay(fact: FactSourceDisplayRef): FactSourceDisplay | null {
  const sourceType = nonEmpty(fact.sourceType);
  if (!sourceType) return null;

  const sourceMessageId = nonEmpty(fact.source_message_id);
  const sourceMessageLabel = nonEmpty(fact.source_message_label);
  const icon = SOURCE_ICON_BY_TYPE[sourceType] ?? "info";

  if (sourceType === "customer_email" && sourceMessageId) {
    return {
      icon,
      label: sourceMessageLabel,
      labelKey: sourceMessageLabel ? "status.factSource.customer_email" : "ws.sourceEmailUnknown",
    };
  }

  return {
    icon,
    label: null,
    labelKey: `status.factSource.${sourceType}`,
  };
}

export function factProvenanceItems(fact: FactProvenanceRef): FactProvenanceItem[] {
  const items: FactProvenanceItem[] = [];
  const overrideReason = nonEmpty(fact.override_reason ?? fact.overrideReason);

  if (overrideReason) {
    items.push({ key: "override_reason", labelKey: "ws.manualOverrideReason", value: overrideReason });
  }
  return items;
}

export type DraftRevealRef = {
  id?: string | null;
  review_state?: string | null;
  stale_reason?: string | null;
  is_current_decision?: boolean | null;
  body_text?: string | null;
};

/**
 * Whether this presentation of a draft is one that may be revealed character by character (#804).
 *
 * The animation says "this was just produced for you", so it must only ever run where that is
 * true. Every clause below is a way for it to be false, and the default is not to animate: a
 * missed reveal costs nothing, while an animation on a reply that already went out, or on a draft
 * being read back out of history, is the interface lying about what the operator is looking at.
 */
export function draftRevealEligible(input: {
  draft: DraftRevealRef | null | undefined;
  /** The history view is a record of what happened; nothing in it is new. */
  historical: boolean;
  /** The editor is a text box the operator is typing in, not a presentation. */
  editing: boolean;
  delivery: ActionDeliveryRef;
  /** A newer inbound message arrived after this draft was written. */
  overtakenByNewerMessage?: boolean;
}): boolean {
  const draft = input.draft;
  if (!draft?.id) return false;
  // Nothing to reveal, and an empty animation would just be a pause.
  if (!draft.body_text) return false;
  if (input.historical || input.editing) return false;
  if (input.overtakenByNewerMessage) return false;
  // Anything other than `pending` means a person has already been here — edited it, approved it,
  // or turned it down — so it is not being seen for the first time whatever the record says.
  if (draft.review_state !== "pending") return false;
  if (draft.stale_reason) return false;
  if (draft.is_current_decision === false) return false;
  if (isTerminalDeliveryForDraft({ id: draft.id }, input.delivery)) return false;
  return true;
}
